import {
  createStyles,
  makeStyles,
  Theme,
  UIBox,
  UICircularProgress,
  UIDialog,
  UIDialogActions,
  UIDialogContent,
  UIDialogTitle
} from "../../_core/components";
import { useState } from "react";
import { resetPassword } from "aws-amplify/auth";
import UITypography from "../../_core/components/ui/UITypography";
import UIButton from "../../_core/components/ui/UIButton";
import { Check, Email, Security } from "../../_core/components/icons";
import { buildResetPasswordInput } from "../utils";

interface PasswordResetDialogProps {
  open: boolean;
  onClose: () => void;
  username: string;
}

const PasswordResetDialog = ({ open, username, onClose }: PasswordResetDialogProps) => {
  const classes = useStyles();

  const [isLoading, setIsLoading] = useState(false);
  const [emailSent, setEmailSent] = useState(false);

  const handleResetPassword = async () => {
    setIsLoading(true);
    try {
      await resetPassword(buildResetPasswordInput(username));
      setEmailSent(true);
    } catch (error) {
      console.error("Error resetting password:", error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <UIDialog
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          minWidth: 400,
          maxWidth: 500,
          borderRadius: 2
        }
      }}
    >
      <UIDialogTitle className={classes.dialogTitle}>
        <Security color="warning" />
        <UITypography variant="h6" component="span">
          Password Reset Required
        </UITypography>
      </UIDialogTitle>

      <UIDialogContent className={classes.dialogContent}>
        <UIBox className={classes.contentBox}>
          {emailSent ? (
            <>
              <UITypography variant="body1" className={classes.description}>
                We've sent you an email with a link to reset your password. Please check your inbox (and
                spam folder just in case)
                and follow the instructions to create a new password.
              </UITypography>
              <UIBox className={classes.infoBox}>
                <Check color="success" fontSize="small" />
                <UITypography variant="body2" color="text.secondary">
                  A reset link was sent to your registered email address
                </UITypography>
              </UIBox>
            </>
          ) : (
            <>
              <UITypography variant="body1" className={classes.description}>
                Your password no longer meets our updated security policy and must be changed before you
                can continue.
              </UITypography>
              <UIBox className={classes.infoBox}>
                <Email color="primary" fontSize="small" />
                <UITypography variant="body2" color="text.secondary">
                  A reset link will be sent to your registered email address
                </UITypography>
              </UIBox>
            </>
          )}
        </UIBox>
      </UIDialogContent>

      <UIDialogActions className={classes.dialogActions}>
        <UIButton
          variant="contained"
          color="primary"
          size="large"
          fullWidth
          onClick={handleResetPassword}
          disabled={isLoading || emailSent}
          startIcon={isLoading ? <UICircularProgress size={20} /> : null}
          className={classes.resetButton}
        >
          {isLoading ? "Sending Reset Link..." : "Reset Password"}
        </UIButton>
      </UIDialogActions>
    </UIDialog>
  );
};

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    dialog: {
      "& .MuiPaper-root": {
        minWidth: 400,
        maxWidth: 500,
        borderRadius: 8
      }
    },
    dialogTitle: {
      display: "flex",
      alignItems: "center",
      gap: 8,
      paddingBottom: 8
    },
    dialogContent: {
      paddingTop: 16
    },
    contentBox: {
      marginBottom: 16
    },
    description: {
      marginBottom: 16
    },
    infoBox: {
      marginTop: 25,
      display: "flex",
      alignItems: "center",
      gap: 8,
      padding: 16,
      backgroundColor: "#fafafa",
      borderRadius: 4,
      border: "1px solid #e0e0e0"
    },
    dialogActions: {
      padding: 24,
      paddingTop: 8
    },
    resetButton: {
      paddingTop: 12,
      paddingBottom: 12,
      textTransform: "none",
      fontSize: "1rem",
      fontWeight: 600
    }
  })
);


export default PasswordResetDialog;
